import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Bell,
  CheckCircle,
  AlertTriangle,
  Info,
  AlertCircle,
  CheckCheck,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { DashboardLayout } from '../components/DashboardLayout';
import { useEntityStore, useAlertsStore } from '../store';
import { alertsAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { formatDistanceToNow } from 'date-fns';

const priorityConfig = {
  info: { icon: Info, color: 'priority-info', bgColor: 'bg-blue-50 dark:bg-blue-950' },
  medium: { icon: AlertCircle, color: 'priority-medium', bgColor: 'bg-yellow-50 dark:bg-yellow-950' },
  high: { icon: AlertTriangle, color: 'priority-high', bgColor: 'bg-orange-50 dark:bg-orange-950' },
  critical: { icon: AlertCircle, color: 'priority-critical', bgColor: 'bg-red-50 dark:bg-red-950' },
};

function AlertCard({ alert, onMarkRead }) {
  const config = priorityConfig[alert.priority] || priorityConfig.info;
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'p-5 rounded-xl border transition-all hover:shadow-md',
        !alert.is_read && 'border-primary/50',
        config.bgColor
      )}
    >
      <div className="flex items-start gap-4">
        <div className={cn('p-2 rounded-lg', !alert.is_read && 'animate-pulse')}>
          <Icon className={cn('h-5 w-5', config.color.replace('priority-', 'text-').replace('priority', ''))} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={config.color}>{alert.priority}</Badge>
            <Badge variant="outline">{alert.type.replace('_', ' ')}</Badge>
            {!alert.is_read && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
          </div>
          <h3 className="font-semibold">{alert.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
          <p className="text-xs text-muted-foreground mt-2">
            {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
          </p>
        </div>
        {!alert.is_read && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMarkRead(alert.id)}
            className="flex-shrink-0"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Mark Read
          </Button>
        )}
      </div>
    </motion.div>
  );
}

export function AlertsPage() {
  const { selectedEntity, entities } = useEntityStore();
  const { alerts, setAlerts, markRead, markAllRead } = useAlertsStore();
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchAlerts = async () => {
    try {
      // Pass entity UUID — resolve_entity_filter handles both UUID and type code formats
      const { data } = await alertsAPI.getAll({ entity_id: selectedEntity !== 'all' ? selectedEntity : undefined });
      setAlerts(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      setIsLoading(true);
      fetchAlerts();
    }
  }, [selectedEntity, entities]);

  const handleMarkRead = async (alertId) => {
    try {
      await alertsAPI.markRead(alertId);
      markRead(alertId);
      toast.success('Alert marked as read');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const ent2 = selectedEntity !== 'all' ? entities.find((e) => e.id === selectedEntity) : null;
      await alertsAPI.markAllRead(ent2 ? ent2.type : undefined);
      markAllRead();
      toast.success('All alerts marked as read');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRunChecks = async () => {
    setIsLoading(true);
    try {
      await alertsAPI.runChecks();
      await fetchAlerts();
      toast.success('Alert checks completed');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAlerts = alerts.filter((alert) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !alert.is_read;
    return alert.priority === filter;
  });

  const stats = {
    total: alerts.length,
    unread: alerts.filter((a) => !a.is_read).length,
    critical: alerts.filter((a) => a.priority === 'critical' && !a.is_read).length,
    high: alerts.filter((a) => a.priority === 'high' && !a.is_read).length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="alerts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Alerts</h1>
            <p className="text-muted-foreground mt-1">Notifications and action items</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRunChecks}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Run Checks
            </Button>
            {stats.unread > 0 && (
              <Button onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card
            className={cn('cursor-pointer transition-all', filter === 'all' && 'ring-2 ring-primary')}
            onClick={() => setFilter('all')}
          >
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card
            className={cn('cursor-pointer transition-all', filter === 'unread' && 'ring-2 ring-primary')}
            onClick={() => setFilter('unread')}
          >
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Unread</p>
              <p className="text-2xl font-bold text-blue-600">{stats.unread}</p>
            </CardContent>
          </Card>
          <Card
            className={cn('cursor-pointer transition-all', filter === 'critical' && 'ring-2 ring-primary')}
            onClick={() => setFilter('critical')}
          >
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Critical</p>
              <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
            </CardContent>
          </Card>
          <Card
            className={cn('cursor-pointer transition-all', filter === 'high' && 'ring-2 ring-primary')}
            onClick={() => setFilter('high')}
          >
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">High Priority</p>
              <p className="text-2xl font-bold text-orange-600">{stats.high}</p>
            </CardContent>
          </Card>
        </div>

        {/* Alert List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredAlerts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Alerts</h3>
              <p className="text-muted-foreground">
                {filter === 'all' ? 'No alerts to display' : `No ${filter} alerts`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredAlerts.map((alert, index) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onMarkRead={handleMarkRead}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
